import { config } from '../config/environment';

const BASE = `${config.apiBaseUrl}/invoices`;
const PUBLIC_BASE = `${config.apiBaseUrl}/public/invoices`;

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

// ── Types ──────────────────────────────────────────────────────────────────────

export interface LinkPaymentAccount {
  platform: string;
  handle: string;
}

export interface Invoice {
  id: string;
  invoice_number: string;
  appointment_id: string | null;
  package_payment_id: string | null;
  client_id: string;
  client_name: string;
  client_email: string;
  coach_id: string | null;
  coach_name: string;
  session_date: string | null;
  session_duration_minutes: number | null;
  meeting_name: string | null;
  package_name: string | null;
  installment_number: number | null;
  payment_type: string | null;
  amount_cents: number;
  currency: string;
  status: 'draft' | 'sent' | 'viewed' | 'paid' | 'overdue' | 'void';
  due_date: string | null;
  sent_at: string | null;
  viewed_at: string | null;
  paid_at: string | null;
  voided_at: string | null;
  payment_url: string;
  created_at: string;
  description: string | null;
  notes: string | null;
  payment_status: string | null;
  payment_failure_reason: string | null;
}

export interface PublicInvoice {
  invoice_number: string;
  coach_name: string;
  client_name: string;
  session_date: string | null;
  session_duration_minutes: number | null;
  description: string | null;
  amount_cents: number;
  currency: string;
  status: string;
  due_date: string | null;
  paid_at: string | null;
  notes: string | null;
  link_payment_accounts: LinkPaymentAccount[];
  stripe_enabled: boolean;
  stripe_publishable_key: string | null;
  stripe_account_id: string | null;
  appointment_id: string | null;
  client_id: string | null;
  coach_id: string | null;
  payment_status: string | null;
  payment_failure_reason: string | null;
}

export interface AuthedInvoice extends PublicInvoice {
  id: string;
  appointment_id: string | null;
  saved_card_last4: string | null;
  saved_card_brand: string | null;
  saved_payment_method_id: string | null;
  stripe_customer_id: string | null;
}

export interface InvoiceCreateParams {
  appointment_id: string;
  due_date?: string | null;
  description?: string | null;
  notes?: string | null;
}

// ── Coach API ──────────────────────────────────────────────────────────────────

export async function createInvoice(params: InvoiceCreateParams): Promise<Invoice> {
  const res = await fetch(BASE, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(params),
  });
  return handleResponse<Invoice>(res);
}

export async function getInvoiceForSession(appointmentId: string): Promise<Invoice | null> {
  const res = await fetch(`${BASE}/session/${appointmentId}`, { headers: authHeaders() });
  if (res.status === 404) return null;
  return handleResponse<Invoice | null>(res);
}

export async function listInvoices(filters?: {
  status?: string;
  client_id?: string;
  date_from?: string;
  date_to?: string;
}): Promise<Invoice[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.client_id) params.set('client_id', filters.client_id);
  if (filters?.date_from) params.set('date_from', filters.date_from);
  if (filters?.date_to) params.set('date_to', filters.date_to);
  const res = await fetch(`${BASE}?${params}`, { headers: authHeaders() });
  return handleResponse<Invoice[]>(res);
}

export async function sendInvoice(invoiceId: string): Promise<Invoice> {
  const res = await fetch(`${BASE}/${invoiceId}/send`, { method: 'POST', headers: authHeaders() });
  return handleResponse<Invoice>(res);
}

export async function resendInvoice(invoiceId: string): Promise<Invoice> {
  const res = await fetch(`${BASE}/${invoiceId}/resend`, { method: 'POST', headers: authHeaders() });
  return handleResponse<Invoice>(res);
}

export async function voidInvoice(invoiceId: string): Promise<Invoice> {
  const res = await fetch(`${BASE}/${invoiceId}/void`, { method: 'POST', headers: authHeaders() });
  return handleResponse<Invoice>(res);
}

// ── Unbilled items ─────────────────────────────────────────────────────────────

export interface UnbilledItem {
  item_type: 'session' | 'package_payment';
  id: string;
  enrollment_id: string | null;
  client_id: string;
  client_name: string;
  amount_cents: number;
  currency: string;
  date: string | null;
  description: string;
}

export async function getUnbilledItems(filters?: {
  client_id?: string;
  date_from?: string;
  date_to?: string;
  item_type?: 'session' | 'package_payment';
}): Promise<UnbilledItem[]> {
  const params = new URLSearchParams();
  if (filters?.client_id) params.set('client_id', filters.client_id);
  if (filters?.date_from) params.set('date_from', filters.date_from);
  if (filters?.date_to) params.set('date_to', filters.date_to);
  if (filters?.item_type) params.set('item_type', filters.item_type);
  const qs = params.toString();
  const res = await fetch(`${BASE}/unbilled${qs ? `?${qs}` : ''}`, { headers: authHeaders() });
  return handleResponse<UnbilledItem[]>(res);
}

// ── Client portal ──────────────────────────────────────────────────────────────

export async function listMyInvoices(): Promise<Invoice[]> {
  const res = await fetch(`${BASE}/my`, { headers: authHeaders() });
  return handleResponse<Invoice[]>(res);
}

export async function getInvoiceByTokenAuthed(token: string): Promise<AuthedInvoice> {
  const res = await fetch(`${BASE}/token/${token}`, { headers: authHeaders() });
  return handleResponse<AuthedInvoice>(res);
}

// ── Public (no auth) ───────────────────────────────────────────────────────────

export async function getPublicInvoice(token: string): Promise<PublicInvoice> {
  const res = await fetch(`${PUBLIC_BASE}/${token}`);
  return handleResponse<PublicInvoice>(res);
}
